import { useEffect, useState } from "react";
import { useKmsService } from "../providers/kms-service-provider";
import { Collaborateur } from "../types/models";
import { Entreprise } from "../types/models/entreprise";
import { driverToCollaborateur } from "../utils/driver-to-collaborateur";

/**
 * Entreprises pouvant porter une réservation : le demandeur peut être un
 * contact d'une entreprise cliente (collaborateur) ou partenaire (chauffeur),
 * on propose donc les deux listes fusionnées.
 *
 * `GET /entreprises/client` et `GET /entreprises/partenaire` sont réservés au back-office :
 * `enabled` doit rester à `false` pour tout appelant non-admin (403 sinon). Utile uniquement pour
 * le `<Select>` d'entreprise à la création admin — pas pour retrouver une entreprise déjà connue
 * par son id, où `entrepriseService.getEntrepriseById` (ouvert à tout utilisateur connecté) suffit.
 */
export const useEntreprisesList = (enabled: boolean) => {
  const { entrepriseService } = useKmsService();
  const [entreprises, setEntreprises] = useState<Entreprise[]>([]);

  useEffect(() => {
    if (!enabled) {
      setEntreprises([]);
      return;
    }

    Promise.all([
      entrepriseService.getEntreprisesClientes(),
      entrepriseService.getEntreprisesPartenaires(),
    ])
      .then(([clientes, partenaires]) => {
        const merged = new Map<number, Entreprise>();
        [...clientes, ...partenaires].forEach((entreprise) =>
          merged.set(entreprise.idEntreprise, entreprise),
        );
        setEntreprises(Array.from(merged.values()));
      })
      .catch(() => setEntreprises([]));
  }, [enabled, entrepriseService]);

  return entreprises;
};

/**
 * Carnet d'adresse d'une entreprise sélectionnée, utilisable comme passager.
 *
 * `GET /entreprises/{id}/passagers` couvre déjà collaborateurs, gestionnaires
 * et externes d'une entreprise cliente — mais pas les chauffeurs d'une
 * entreprise partenaire, chargés séparément. Malgré son nom, la route renvoie
 * des ResponseCollaborateurDto (id et idContact distincts).
 */
export const useEntrepriseContacts = (
  entreprise: Pick<Entreprise, "idEntreprise" | "isPartenaire"> | undefined,
  enabled: boolean,
) => {
  const { entrepriseService } = useKmsService();

  const [passagers, setPassagers] = useState<Collaborateur[]>([]);
  const [chauffeurs, setChauffeurs] = useState<Collaborateur[]>([]);

  const idEntreprise = entreprise?.idEntreprise;
  const isPartenaire = entreprise?.isPartenaire;

  useEffect(() => {
    if (!enabled || !idEntreprise) {
      setPassagers([]);
      setChauffeurs([]);
      return;
    }

    if (isPartenaire) {
      entrepriseService
        .getDriversFromCompany(Number(idEntreprise))
        .then((drivers) => setChauffeurs(drivers.map(driverToCollaborateur)))
        .catch(() => setChauffeurs([]));
    } else {
      setChauffeurs([]);
    }

    entrepriseService
      .getContactsFromCompany(Number(idEntreprise))
      .then(setPassagers)
      .catch(() => setPassagers([]));
  }, [enabled, idEntreprise, isPartenaire, entrepriseService]);

  const passagersSansGestionnaires = passagers.filter(
    (collaborateur) => !collaborateur.isGestionnaire,
  );
  const idContactsDuCarnet = new Set(
    passagersSansGestionnaires.map((passager) => passager.idContact),
  );

  const carnet: Collaborateur[] = [
    ...passagersSansGestionnaires,
    ...chauffeurs
      .filter((chauffeur) => !idContactsDuCarnet.has(chauffeur.idContact))
      .map((chauffeur) => ({
        ...chauffeur,
        isClient: false,
        isChauffeur: true,
      })),
  ];

  return { passagers, chauffeurs, carnet };
};
